import axios from 'axios';

import { sendRequest, DATA_URL, EXCEL_TO_CSV_URL, PROJECTS_URL, UPLOAD_FILE } from 'api/utils';
import { buildErrorMessage } from 'api/utils/helpers';
import { Dictionary } from 'environment';
import {
	LicenseLimitationErrorTypes,
	toggleFreeLicenseLimitationModalEvent
} from 'helpers/licenses';
import {
	CreateProject,
	Project,
	ProjectMetadataDefinition,
	UpdateProject
} from 'store/data/projects';
import { parseApiProject, parseApiProjects } from 'store/data/projects/parsers';
import { LedidiStatusCode, StringMap } from 'types/index';
import { RequestTypes } from 'api/utils';

import {
	ConvertXlsToCsvInput,
	CreateProjectResponse,
	GetProjectResponse,
	GetProjectsResponse,
	UploadProjectDatasetInput,
	UpdateExistingDatasetInput,
	GetLeaveProjectResponse,
	GenerateVariableNamesInput,
	GenerateVariableNamesResponse,
	CheckTransactionProgressResponse,
	ConvertXlsToCsvAsyncResponse,
	ConvertXlsToCsvAsyncData,
	SuggestVariableTypeInput,
	ImportVariableTypesAsyncResponse,
	DraggedFile,
	//
	GetProjectsCollaboratorsAvatarDataRequest,
	GetProjectsCollaboratorsAvatarDataResponse,
	GetProjectsCollaboratorsAvatarDataOutput,
	//
	UpdateProjectInput,
	UpdateProjectRequest,
	UpdateProjectResponse,
	//
	CopyProjectOutput,
	CopyProjectInput,
	CopyProjectRequest,
	CopyProjectResponse,
	//
	UploadUrlInput,
	UploadUrlOutput,
	SuggestedVariableTypesResponse,
	ImportAddMoreEntriesAsyncResponse,
	TransferProjectsOwnershipRequest,
	GetProjectMetadataDefinitionResponse
} from './types';
import { ProjectStatus, ProjectType } from 'types/data/projects/constants';

export const methods = {
	getProjects: 'getListOfAllProjects',
	getProject: 'getProject',
	getCounts: 'getObservationCountsForAllProjects',
	createProject: 'addProject',
	updateProject: 'updateProject',
	deleteProject: 'deleteProject',
	leaveProject: 'leaveProject',
	copyProject: 'copyProject',
	/////////////////////////////
	convertXlsToCsv: 'xlsxtocsv',
	convertXlsToCsvAsync: 'xlsxToCSVHeader',
	createDataset: 'createNewDatasetTypeFromCSVString',
	createDatasetAsync: 'start_import/full/',
	importToExistingDataset: 'start_import/add_more_entries/',
	updateExistingDataset: 'start_import/add_more_columns/',
	generateVariableNames: 'generateVariableNames',
	checkImportProgress: 'trackTransactionProgress',
	suggestVariableTypes: 'suggest_variable_types/',
	getProjectsCollaboratorsAvatarData: 'getCollaboratorsForProjectList',
	convertProject: 'convertProject',
	uploadUrl: 'upload_url/',

	// transfer project
	transferProjectsOwnership: 'transferProjectsOwnership', // only for Enterprise users

	// project metadata
	getProjectMetadataDefinition: 'getProjectMetadataDefinition'
};

export default () => ({
	async getProjects(): Promise<Project[]> {
		const { data }: GetProjectsResponse = await sendRequest(PROJECTS_URL, {
			method: methods.getProjects,
			retrieveProjectOwnerDetails: true
		});

		// I am not responsible for this code, backward compatability
		if ((data?.statusCode ?? '200') !== '200') {
			throw new Error(Dictionary.errors.api.projects.couldNotLoadProjects);
		}

		// TODO: remove when BE returns `projectType`
		data.projects.forEach(project => {
			project.projectType = ProjectType.CORE;
		});

		return parseApiProjects(data.projects);
	},

	async getProject(projectId: string): Promise<Project> {
		try {
			const { data }: GetProjectResponse = await sendRequest(PROJECTS_URL, {
				method: methods.getProject,
				retrieveProjectOwnerDetails: true,
				project: {
					projectId
				}
			});

			const parsed = parseApiProject(data.project);
			if (
				[
					ProjectStatus.Frozen,
					ProjectStatus.Rejected,
					ProjectStatus.PendingAdminApproval
				].includes(parsed.status)
			) {
				// We do not allow opening projects with these statuses.
				throw new Error(Dictionary.errors.api.projects.couldNotLoadProject);
			}
			return { ...parsed, projectActions: data.projectActions };
		} catch (e) {
			throw new Error(Dictionary.errors.api.projects.couldNotLoadProject);
		}
	},

	async createProject(createProject: CreateProject): Promise<Project> {
		const { codelistEnabled, ...project } = createProject;
		const { data }: CreateProjectResponse = await sendRequest(PROJECTS_URL, {
			method: methods.createProject,
			project,
			codelistEnabled
		});

		if (!data.project) {
			if (data.ledidiStatusCode === LedidiStatusCode.ErrorLicense) {
				toggleFreeLicenseLimitationModalEvent().dispatch(
					LicenseLimitationErrorTypes.createProject
				);
				throw new Error();
			}

			throw new Error(Dictionary.errors.api.projects.couldNotCreateProject);
		}

		return parseApiProject(data.project);
	},

	async updateProject(input: UpdateProjectInput): Promise<UpdateProject> {
		const { codelistEnabled, ...project } = input.project;
		const { data } = await sendRequest<UpdateProjectRequest, UpdateProjectResponse>(
			PROJECTS_URL,
			{
				method: methods.updateProject,
				project,
				codelistEnabled
			}
		);

		if (data.statusCode !== '200') throw new Error();
		return data.project;
	},

	async leaveProject(projectId: string): Promise<any> {
		const { data }: GetLeaveProjectResponse = await sendRequest(PROJECTS_URL, {
			method: methods.leaveProject,
			project: { projectId }
		});

		if (!data) {
			throw new Error(Dictionary.errors.api.projects.couldNotLeaveProject);
		}

		return data;
	},

	async copyProject(input: CopyProjectInput): Promise<CopyProjectOutput> {
		const { data } = await sendRequest<CopyProjectRequest, CopyProjectResponse>(DATA_URL, {
			method: methods.copyProject,
			...input
		});

		if (data.httpStatusCode !== 200) {
			if (data.ledidiStatusCode === LedidiStatusCode.ErrorLicense) {
				toggleFreeLicenseLimitationModalEvent().dispatch(
					input.newOwnerId
						? LicenseLimitationErrorTypes.transferProject
						: LicenseLimitationErrorTypes.copyProject
				);
				throw new Error();
			}

			throw new Error(Dictionary.errors.api.projects.couldNotCopyProject);
		}

		return data;
	},

	//////////////////////////////////////////////////////////
	//////////////////////////////////////////////////////////

	async convertXlsToCsvAsync(
		input: ConvertXlsToCsvInput
	): Promise<ConvertXlsToCsvAsyncData | undefined> {
		try {
			const { data }: ConvertXlsToCsvAsyncResponse = await sendRequest(
				EXCEL_TO_CSV_URL,
				{
					method: methods.convertXlsToCsvAsync,
					...input.body
				},
				{ ...input.config, timeout: 29000 }
			);

			if (data && data.csvHeaderContent) {
				return data;
			}

			throw new Error(Dictionary.errors.api.projects.couldNotConvert);
		} catch (e: any) {
			if (axios.isCancel(e)) {
				console.log(Dictionary.errors.api.projects.requestCanceled, e.message);
			} else {
				throw new Error(e.message);
			}
		}
	},

	async convertXlsToCsv(input: ConvertXlsToCsvInput): Promise<string | undefined> {
		try {
			const { data } = await sendRequest(
				EXCEL_TO_CSV_URL,
				{
					method: methods.convertXlsToCsv,
					...input.body
				},
				input.config
			);

			if (data && data.csvContent) {
				return data.csvContent;
			}
		} catch (e: any) {
			if (axios.isCancel(e)) {
				console.log(Dictionary.errors.api.projects.requestCanceled, e.message);
			} else {
				throw new Error(e.message);
			}
		}
	},

	async uploadUrl(input: UploadUrlInput): Promise<{
		fileId: string;
		url: string;
	}> {
		try {
			const { data }: UploadUrlOutput = await sendRequest(UPLOAD_FILE + methods.uploadUrl, {
				...input
			});

			return data;
		} catch (e) {
			throw new Error(Dictionary.errors.api.projects.couldNotUploadFile);
		}
	},

	async uploadFile(url: string, file: DraggedFile) {
		try {
			await sendRequest(
				url,
				file,
				{
					withCredentials: false,
					headers: {
						'Content-Type': 'application/json'
					}
				},
				RequestTypes.PUT
			);

			return true;
		} catch (e) {
			return false;
		}
	},

	//  [x] [ ] [ ] - DataToEntries
	async updateExistingDataset(input: UpdateExistingDatasetInput) {
		const { data }: ImportVariableTypesAsyncResponse = await sendRequest(
			UPLOAD_FILE + methods.updateExistingDataset,
			{
				...input
			},
			{ timeout: 900000 } // This is a very heavy request that might take very long. More details on PRJCTS-2647
		);

		if (data && data.httpStatusCode !== 200) {
			buildErrorMessage(data);
		}

		return data.transactionId;
	},

	// [ ] [x] [] - EntriesToDataset
	async uploadToExistingDataset(input: UploadProjectDatasetInput) {
		const { data }: ImportAddMoreEntriesAsyncResponse = await sendRequest(
			UPLOAD_FILE + methods.importToExistingDataset,
			{
				...input
			},
			{ timeout: 900000 } // This is a very heavy request that might take very long. More details on PRJCTS-2647
		);

		if (data.ledidiStatusCode === LedidiStatusCode.ErrorLicense) {
			toggleFreeLicenseLimitationModalEvent().dispatch(
				LicenseLimitationErrorTypes.createEntry
			);
			throw new Error();
		}

		if (data.ledidiStatusCode === LedidiStatusCode.ErrorLicenseOther) {
			toggleFreeLicenseLimitationModalEvent().dispatch(
				LicenseLimitationErrorTypes.collaboratorCreateEntry
			);
			throw new Error();
		}

		if (data && data.httpStatusCode !== 200) {
			buildErrorMessage(data);
		}

		return data.transactionIds;
	},

	// [ ] [ ] [x] - ReplaceAll
	async uploadProjectDataset(input: UploadProjectDatasetInput) {
		const { data }: ImportVariableTypesAsyncResponse = await sendRequest(
			UPLOAD_FILE + methods.createDatasetAsync,
			{
				...input
			},
			{ timeout: 29000 } // This is a very heavy request that might take very long. More details on PRJCTS-2647
		);

		if (data.ledidiStatusCode === LedidiStatusCode.ErrorLicense) {
			toggleFreeLicenseLimitationModalEvent().dispatch(
				LicenseLimitationErrorTypes.createEntry
			);
			throw new Error();
		}

		if (data.ledidiStatusCode === LedidiStatusCode.ErrorLicenseOther) {
			toggleFreeLicenseLimitationModalEvent().dispatch(
				LicenseLimitationErrorTypes.collaboratorCreateEntry
			);
			throw new Error();
		}

		if (data && data.httpStatusCode !== 200) {
			buildErrorMessage(data);
		}

		return data.transactionId;
	},

	async checkTransactionProgress(
		projectId: number,
		transactionId: number,
		propagateErrors?: boolean
	) {
		const { data }: CheckTransactionProgressResponse = await sendRequest(DATA_URL, {
			method: methods.checkImportProgress,
			projectId,
			transactionId
		});

		const { response } = data;

		if (response && response.errors && response.errors.length && !propagateErrors) {
			throw new Error(Dictionary.errors.api.projects.couldNotCompleteImport);
		}

		return data;
	},

	async generateVariableNames({
		variableLabels,
		variableNames,
		projectId
	}: GenerateVariableNamesInput): Promise<StringMap | undefined> {
		const { data }: GenerateVariableNamesResponse = await sendRequest(DATA_URL, {
			method: methods.generateVariableNames,
			variableLabels,
			existingNames: variableNames,
			projectId
		});

		if (data && data.httpStatusCode === 200) {
			return data.generated;
		}
	},

	async suggestVariableTypesUrl({
		fileId,
		projectId
	}: SuggestVariableTypeInput): Promise<number | undefined> {
		const { data }: ImportVariableTypesAsyncResponse = await sendRequest(
			UPLOAD_FILE + methods.suggestVariableTypes,
			{
				projectId,
				fileId
			}
		);

		if (data.httpStatusCode === 200) {
			return data.transactionId;
		}
	},

	async getSuggestedVariableTypes(
		url: string
	): Promise<SuggestedVariableTypesResponse | undefined> {
		try {
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json'
				}
			});
			const { json } = response;
			return json.bind(response)();
		} catch (e) {
			throw new Error(Dictionary.errors.api.projects.couldNotUploadFile);
		}
	},

	async getProjectsCollaboratorsAvatarData(): Promise<GetProjectsCollaboratorsAvatarDataOutput> {
		const { data } = await sendRequest<
			GetProjectsCollaboratorsAvatarDataRequest,
			GetProjectsCollaboratorsAvatarDataResponse
		>(PROJECTS_URL, {
			method: methods.getProjectsCollaboratorsAvatarData
		});

		// TODO: UNCOMMENT WHEN RESPONSE CHANGES
		// if (data.httpStatusCode !== 200) throw new Error();

		// TODO: UNCOMMENT WHEN RESPONSE CHANGES
		// if (data.httpStatusCode !== 200) throw new Error();

		if (!data.projectUserTree) throw new Error();

		const output: GetProjectsCollaboratorsAvatarDataOutput = {
			data: {
				byProjectId: {}
			}
		};

		/**
		 * Parse result to a more meaningful structure
		 */
		Object.keys(data.projectUserTree).forEach(projectId => {
			output.data.byProjectId[projectId] = {
				byUserId: data.projectUserTree[projectId]
			};
		});

		return output;
	},

	// Only for enterprise users
	transferProjectsOwnership: async (userId: string, projectIds: string[]): Promise<void> => {
		try {
			const { data } = await sendRequest<TransferProjectsOwnershipRequest>(PROJECTS_URL, {
				method: methods.transferProjectsOwnership,
				userId,
				projectIds
			});
			if (data.statusCode !== '200') throw new Error();
		} catch (e) {
			throw new Error(Dictionary.admin.enterprise.errors.api.transferProjectOwnership);
		}
	},

	async getProjectMetadataDefinition(): Promise<ProjectMetadataDefinition> {
		const { data }: GetProjectMetadataDefinitionResponse = await sendRequest(PROJECTS_URL, {
			method: methods.getProjectMetadataDefinition
		});

		if (!data.metadataDefinition) return {} as ProjectMetadataDefinition;

		return {
			metadataDefinition: data.metadataDefinition,
			metadataSchema: data.metadataSchema
		};
	}
});
